import { CustomButton } from '@/shared/ui/components';
import { Modal } from 'antd';
import { forwardRef } from 'react';
import './confirmYourActivityModal.scss';
import { useConfirmYourActivityModal } from './useConfirmYourActivityModal';

export interface IProps {
  open: boolean;
  handleConfirmClick: () => void;
  handleSessionTimedOutModalDisplay: () => void;
  loading: boolean;
}

/**
 * Inactivity warning dialog asking the user to confirm activity before the session expires. Forwards the open flag, the confirm handler, the session-timed-out callback and the ref into useConfirmYourActivityModal, then renders a non-dismissable Modal whose confirm button shows the remaining seconds countdown and a loading state.
 *
 * @param props - Open flag, loading flag, confirm handler and session-timed-out callback.
 * @param ref - Forwarded ref the hook exposes its imperative handle on.
 * @returns The rendered modal element.
 */
const ConfirmYourActivityModal = forwardRef<unknown, IProps>((props, ref) => {
  const { open, loading } = props;
  const { state, handleConfirmClick } = useConfirmYourActivityModal(
    {
      open: props.open,
      handleConfirmClick: props.handleConfirmClick,
      handleSessionTimedOutModalDisplay: props.handleSessionTimedOutModalDisplay,
    },
    ref,
  );

  return (
    <Modal
      open={open}
      destroyOnClose
      width={400}
      closable={false}
      maskClosable={false}
      keyboard={false}
      title="Confirm your activity"
      className="confirm-your-activity-modal"
      footer={null}
    >
      <div className="confirm-your-activity-modal__content">
        You have been inactive for a while. For your security, your session will expire soon. Please
        confirm your activity to stay signed in.
      </div>
      <div className="action">
        <CustomButton loading={loading} onClick={handleConfirmClick}>
          {`Confirm (${state.remainingSeconds}s)`}
        </CustomButton>
      </div>
    </Modal>
  );
});

export default ConfirmYourActivityModal;
